import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonGroup, Modal, Spinner } from 'react-bootstrap';

type PreviewModalProps = {
    open: boolean;
    title: string;
    url?: string;
    loading: boolean;
    error?: string;
    onDownload: () => void;
    onClose: () => void;
};

const PreviewModal: React.FC<PreviewModalProps> = ({
    open,
    title,
    url,
    loading,
    error,
    onDownload,
    onClose,
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onFs = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onFs);
        return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);

    const openInNewTab = () => {
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const toggleFullscreen = async () => {
        if (!fsRef.current) return;
        if (!document.fullscreenElement) await fsRef.current.requestFullscreen?.();
        else await document.exitFullscreen?.();
    };

    return (
        <Modal show={open} onHide={onClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                <div
                    ref={fsRef}
                    style={{
                        height: isFullscreen ? '100vh' : '80vh',
                        width: '100%',
                        background: '#fff',
                    }}
                >
                    {loading && (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                            <Spinner />
                            <small className="mt-2">Loading preview</small>
                        </div>
                    )}
                    {!loading && error && <div className="text-danger p-3">{error}</div>}
                    {!loading && !error && url && (
                        <iframe
                            title="PDF preview"
                            src={url}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                        />
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <ButtonGroup>
                    <Button variant="outline-primary" onClick={openInNewTab} disabled={!url}>
                        <i className="bi bi-box-arrow-up-right me-1" />
                        Open in new tab
                    </Button>
                    <Button variant="outline-primary" onClick={toggleFullscreen} disabled={!url}>
                        <i
                            className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'} me-1`}
                        />
                        {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    </Button>
                    <Button variant="outline-primary" onClick={onDownload} disabled={!url}>
                        <i className="bi bi-download me-1" />
                        Download
                    </Button>
                </ButtonGroup>

                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PreviewModal;
